import * as React from 'react';
import { Store } from './stores';
import { inject, observer } from 'mobx-react';
import * as styles from './FeedWidget.css';
import { LocalizationService } from '../../services/LocalizationService';
import { IAButton } from '../button/Button';
import { IACheckbox } from '../checkbox/Checkbox';
import { IATextField } from '../textField/TextField';
import InstanceService from '../../services/InstanceService';
import FeedService from '../../services/FeedService';
import { IASpinner } from '../spinner/Spinner';
import UserService from '../../services/UserService';
import { IAIcon } from '../icon/Icon';
import { Helper } from '../../Helper';

export interface IProps {
  store?: Store;
}

export interface IState {
  loading: boolean;
  hasAccessRights: boolean;
  selectedSlide: number;
  availableInstances?: any[];
  saving: boolean;
  mode: 'create' | 'edit' | 'delete';
}

@inject('store')
@observer
export class FeedWidget extends React.Component<IProps, IState> {

  private readonly localizationService: LocalizationService = new LocalizationService();

  constructor(props: IProps) {
    super(props);
    this.state = {
      loading: true,
      hasAccessRights: undefined,
      selectedSlide: 1,
      saving: false,
      mode: undefined
    }
    this.localizationService?.checkLocalizedStrings().then(() => this.forceUpdate());
  }

  public componentDidMount(): void {
    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);
    const feedId = urlParams.get("feedId");

    if (feedId == null) {
      this.setState({ mode: 'create' }, () => {
        this.getData();
      });
    } else {
      this.setState({ selectedSlide: urlParams.get("mode") == 'edit' ? 2 : 1 });
      this.setState({ mode: urlParams.get("mode") == 'edit' ? 'edit' : 'delete' }, () => {
        this.getData(feedId);
      });
    }
  }

  private async getData(feedId?: string): Promise<void> {
    const isIntraActiveSiteOwner = await UserService.isSiteOwner(this.props.store.environment, this.props.store.tenant, this.props.store.token, "IntraActive-Admin");

    if (isIntraActiveSiteOwner) {
      const feed = feedId ? await FeedService.getFeed(this.props.store.environment, this.props.store.tenant, this.props.store.token, feedId) : null;
      const availableInstances = (await InstanceService.getInstances(this.props.store.environment, this.props.store.token, this.props.store.tenant, this.props.store.component, false)).map((instance) => {
        return {
          id: instance.instanceName,
          title: instance.instanceName != null ? instance.title : "Default",
        };
      });

      if (feed) {
        this.props.store.currentFeed = feed;
      }

      this.setState({ availableInstances, hasAccessRights: true, loading: false });
    } else {
      this.setState({ hasAccessRights: false, loading: false });
    }
  }

  public render(): JSX.Element {
    let availableInstances = [];
    if (this.state.availableInstances?.length > 0) {
      const sortedAvailableInstances = Helper.sortAlphabeticalByTitle(this.state.availableInstances);
      sortedAvailableInstances.forEach((instance: any) => {
        if (instance?.id == null) {
          instance.id = "Default";
        }
        availableInstances.push(
          <IACheckbox
            key={instance.name}
            label={instance.title}
            highlightColor={this.props.store.highlightColor}
            disabled={this.props.store.currentFeed.instances.indexOf(instance?.id) === -1 && this.props.store.currentFeed.instances?.length === 3}
            isChecked={this.props.store.currentFeed.instances.indexOf(instance?.id) !== -1}
            onChange={() => {
              const index = this.props.store.currentFeed.instances.indexOf(instance?.id);
              if (index === -1) {
                this.props.store.currentFeed.instances.push(instance?.id);
              } else {
                this.props.store.currentFeed.instances.splice(index, 1);
              }
              this.forceUpdate();
            }}
            styles={{
              float: "left",
              clear: "both",
              marginTop: 10
            }}
          />
        );
      })
    }
    let widgetBoxHeight = 370;
    if (this.state.selectedSlide === 2) {
      widgetBoxHeight = 400;
    }
    if (this.state.selectedSlide === 3) {
      if (this.state.availableInstances?.length > 10) {
        widgetBoxHeight = window.innerHeight - 190;
      } else {
        widgetBoxHeight = 380 + (this.state.availableInstances?.length * 30)
      }
    }
    return (
      <div className={styles.IA_feedWidget}>
        <div
          className={styles.IA_feedWidgetBox}
          style={{
            height: widgetBoxHeight,
            marginTop: `calc(50vh - ${widgetBoxHeight / 2}px)`
          }}
        >
          {this.state.loading &&
            <div
              className={styles.IA_feedWidgetBoxSlide}
              style={{
                opacity: 1,
                pointerEvents: "auto"
              }}
            >
              <IASpinner />
            </div>
          }
          {!this.state.loading && this.state.mode == 'delete' &&
            <>
              <div
                className={styles.IA_feedWidgetBoxSlide}
                style={{
                  opacity: this.state.hasAccessRights && this.state.selectedSlide === 1 ? 1 : 0,
                  pointerEvents: this.state.hasAccessRights && this.state.selectedSlide === 1 ? "auto" : "none"
                }}
              >
                <div className={styles.IA_integrationIllustration}></div>
                <h1 style={{ textAlign: "center" }}>{this.localizationService.strings.PlayAdmin_DeleteHeadline} "{this.props.store.currentFeed.title}"</h1>
                <p style={{ textAlign: "center", padding: "0 30px" }}>{this.localizationService.strings.PlayAdmin_DeleteText}</p>
                <IAButton
                  label={this.localizationService.strings.PlayAdmin_Delete?.toUpperCase()}
                  disbaled={this.state.saving}
                  buttonColor={"gray"}
                  textColor={"#ffffff"}
                  onClick={() => {
                    this.setState({ saving: true });
                    FeedService.deleteFeed(this.props.store.environment, this.props.store.tenant, this.props.store.token, this.props.store.currentFeed.id).then(() => {
                      this.setState({ saving: false, selectedSlide: this.state.selectedSlide + 1 })
                    })
                  }}
                  borderRadius={5}
                  style={{
                    float: "none",
                    display: "block",
                    margin: "30px auto 0 auto",
                    width: 100
                  }}
                />
              </div>
              <div
                className={styles.IA_feedWidgetBoxSlide}
                style={{
                  opacity: this.state.hasAccessRights && this.state.selectedSlide === 2 ? 1 : 0,
                  pointerEvents: this.state.hasAccessRights && this.state.selectedSlide === 2 ? "auto" : "none"
                }}
              >
                <div className={styles.IA_integrationIllustration}></div>
                <h1 style={{ textAlign: "center" }}>{this.localizationService.strings.PlayAdmin_DeletedHeadline}</h1>
                <p style={{ textAlign: "center", padding: "0 30px" }}>{this.localizationService.strings.PlayAdmin_DeletedText}</p>
                <IAButton
                  label={this.localizationService.strings.PlayAdmin_Close?.toUpperCase()}
                  buttonColor={"gray"}
                  textColor={"#ffffff"}
                  onClick={() => {
                    window.close();
                  }}
                  borderRadius={5}
                  style={{
                    float: "none",
                    display: "block",
                    margin: "30px auto 0 auto",
                    width: 100
                  }}
                />
              </div>
              <div
                className={styles.IA_feedWidgetBoxSlide}
                style={{
                  opacity: !this.state.hasAccessRights ? 1 : 0,
                  pointerEvents: !this.state.hasAccessRights ? "auto" : "none"
                }}
              >
                <div className={styles.IA_integrationIllustration}></div>
                <h1 style={{ textAlign: "center" }}>{this.localizationService.strings.PlayAdmin_ConnectIAMessagesWithPlayIssueWithPermissionsHeadline}</h1>
                <p style={{ textAlign: "center" }}>{this.localizationService.strings.PlayAdmin_ConnectIAMessagesWithPlayIssueWithPermissionsText}</p>
              </div>
            </>
          }
          {!this.state.loading && (this.state.mode == 'create' || this.state.mode == 'edit') &&
            <>
              <div
                className={styles.IA_feedWidgetBoxSlide}
                style={{
                  opacity: !this.state.loading && !this.state.hasAccessRights ? 1 : 0,
                  pointerEvents: !this.state.loading && !this.state.hasAccessRights ? "auto" : "none"
                }}
              >
                <div className={styles.IA_integrationIllustration}></div>
                <h1 style={{ textAlign: "center" }}>{this.localizationService.strings.PlayAdmin_ConnectIAMessagesWithPlayIssueWithPermissionsHeadline}</h1>
                <p style={{ textAlign: "center" }}>{this.localizationService.strings.PlayAdmin_ConnectIAMessagesWithPlayIssueWithPermissionsText}</p>
              </div>
              <div
                className={styles.IA_feedWidgetBoxSlide}
                style={{
                  opacity: !this.state.loading && this.state.hasAccessRights && this.state.selectedSlide === 1 ? 1 : 0,
                  pointerEvents: !this.state.loading && this.state.hasAccessRights && this.state.selectedSlide === 1 ? "auto" : "none"
                }}
              >
                <div className={styles.IA_integrationIllustration}></div>
                <h1 style={{ textAlign: "center" }}>{this.localizationService.strings.PlayAdmin_ConnectIAMessagesWithPlayHeadline}</h1>
                <p style={{ textAlign: "center" }}>{this.localizationService.strings.PlayAdmin_ConnectIAMessagesWithPlayText}</p>
                <IAButton
                  label={this.localizationService.strings.PlayAdmin_Cancel?.toUpperCase()}
                  buttonColor={"gray"}
                  textColor={"#ffffff"}
                  onClick={() => {
                    window.close();
                  }}
                  borderRadius={5}
                  style={{
                    float: "left",
                    position: "relative",
                    marginTop: 30,
                    marginLeft: 180,
                    width: 100,
                  }}
                />
                <IAButton
                  label={this.localizationService.strings.PlayAdmin_Next?.toUpperCase()}
                  buttonColor={this.props.store.highlightColor}
                  textColor={"#ffffff"}
                  onClick={() => {
                    this.setState({ selectedSlide: 2 });
                  }}
                  borderRadius={5}
                  style={{
                    float: "left",
                    position: "relative",
                    marginTop: 30,
                    marginLeft: 40,
                    width: 100,
                    textAlign: 'center'
                  }}
                />
              </div>
              <div
                className={styles.IA_feedWidgetBoxSlide}
                style={{
                  opacity: !this.state.loading && this.state.hasAccessRights && this.state.selectedSlide === 2 ? 1 : 0,
                  pointerEvents: !this.state.loading && this.state.hasAccessRights && this.state.selectedSlide === 2 ? "auto" : "none"
                }}
              >
                <div className={styles.IA_integrationIllustration}></div>
                <h1 style={{ textAlign: "center" }}>{this.localizationService.strings.PlayAdmin_FeedName}</h1>
                <IATextField
                  text={this.props.store.currentFeed.title}
                  style={{
                    width: 400,
                    marginLeft: 100
                  }}
                  onChange={(feedName: string) => {
                    this.props.store.currentFeed.title = feedName;
                    this.forceUpdate();
                  }}
                />
                <IAButton
                  label={this.localizationService.strings.PlayAdmin_Previous?.toUpperCase()}
                  buttonColor={"gray"}
                  textColor={"#ffffff"}
                  onClick={() => {
                    this.setState({ selectedSlide: 1 });
                  }}
                  borderRadius={5}
                  style={{
                    float: "left",
                    position: "relative",
                    marginTop: 30,
                    marginLeft: 180,
                    width: 100,
                  }}
                />
                <IAButton
                  label={this.localizationService.strings.PlayAdmin_Next?.toUpperCase()}
                  buttonColor={this.props.store.highlightColor}
                  disbaled={!this.props.store.currentFeed.title}
                  textColor={"#ffffff"}
                  onClick={() => {
                    this.setState({ selectedSlide: 3 });
                  }}
                  borderRadius={5}
                  style={{
                    float: "left",
                    position: "relative",
                    marginTop: 30,
                    marginLeft: 40,
                    width: 100,
                    textAlign: 'center'
                  }}
                />
              </div>
              <div
                className={styles.IA_feedWidgetBoxSlide}
                style={{
                  opacity: !this.state.loading && this.state.hasAccessRights && this.state.selectedSlide === 3 ? 1 : 0,
                  pointerEvents: !this.state.loading && this.state.hasAccessRights && this.state.selectedSlide === 3 ? "auto" : "none"
                }}
              >
                <div className={styles.IA_integrationIllustration}></div>
                <h1 style={{ textAlign: "center" }}>{this.localizationService.strings.PlayAdmin_SelectInstances}</h1>
                <div
                  className={styles.IA_instances}
                  style={{
                    height: widgetBoxHeight - 405
                  }}
                >
                  {availableInstances}
                </div>
                <div className={styles.IA_instanceInfo}>
                  <IAIcon
                    title="Info"
                    size={15}
                    color={"gray"}
                    style={{
                      marginTop: 14
                    }} />
                  <p>{this.localizationService.strings.PlayAdmin_SelectInstancesInfo}</p>
                </div>
                <IAButton
                  label={this.localizationService.strings.PlayAdmin_Previous?.toUpperCase()}
                  buttonColor={"gray"}
                  textColor={"#ffffff"}
                  onClick={() => {
                    this.setState({ selectedSlide: 2 });
                  }}
                  borderRadius={5}
                  style={{
                    float: "left",
                    position: "relative",
                    marginTop: 25,
                    marginLeft: 180,
                    width: 100,
                  }}
                />
                <IAButton
                  label={this.localizationService.strings.PlayAdmin_Save?.toUpperCase()}
                  buttonColor={this.props.store.highlightColor}
                  textColor={"#ffffff"}
                  disbaled={this.props.store.currentFeed.instances?.length < 1 || this.state.saving}
                  onClick={() => {
                    this.setState({ saving: true });
                    switch (this.state.mode) {
                      case 'create':
                        FeedService.createFeed(this.props.store.environment, this.props.store.tenant, this.props.store.token, this.props.store.currentFeed).then((feed) => {
                          this.props.store.currentFeed.id = feed.id;
                          FeedService.generateApiKey(this.props.store.environment, this.props.store.tenant, this.props.store.token).then(() => {
                            this.setState({ selectedSlide: 4, saving: false });
                          });
                        });
                        break;
                      case 'edit':
                        FeedService.updateFeed(this.props.store.environment, this.props.store.tenant, this.props.store.token, this.props.store.currentFeed).then(() => {
                          this.setState({ selectedSlide: 4, saving: false });
                        });
                        break;
                      default:
                        break;
                    }
                  }}
                  borderRadius={5}
                  style={{
                    float: "left",
                    position: "relative",
                    marginTop: 25,
                    marginLeft: 40,
                    width: 100,
                    textAlign: 'center'
                  }}
                />
              </div>
              <div
                className={styles.IA_feedWidgetBoxSlide}
                style={{
                  opacity: !this.state.loading && this.state.hasAccessRights && this.state.selectedSlide === 4 ? 4 : 0,
                  pointerEvents: !this.state.loading && this.state.hasAccessRights && this.state.selectedSlide === 4 ? "auto" : "none"
                }}
              >
                <div className={styles.IA_integrationIllustration}></div>
                <h1 style={{ textAlign: "center" }}>{this.localizationService.strings.PlayAdmin_ConnectedHeadline}</h1>
                <p style={{ textAlign: "center" }}>{this.localizationService.strings.PlayAdmin_ConnectedText}</p>
                <IAButton
                  label={this.localizationService.strings.PlayAdmin_Close?.toUpperCase()}
                  buttonColor={"gray"}
                  textColor={"#ffffff"}
                  onClick={() => {
                    window.close();
                  }}
                  borderRadius={5}
                  style={{
                    float: "none",
                    display: "block",
                    margin: "30px auto 0 auto",
                    width: 100
                  }}
                />
              </div>
              {!this.state.loading && this.state.hasAccessRights &&
                <div className={styles.IA_progreessDots}>
                  <div
                    className={styles.IA_progreessDot}
                    style={{
                      backgroundColor: this.state.selectedSlide === 1 ? "gray" : "lightGray"
                    }}
                  />
                  <div
                    className={styles.IA_progreessDot}
                    style={{
                      backgroundColor: this.state.selectedSlide === 2 ? "gray" : "lightGray"
                    }}
                  />
                  <div
                    className={styles.IA_progreessDot}
                    style={{
                      backgroundColor: this.state.selectedSlide > 2 ? "gray" : "lightGray"
                    }}
                  />
                </div>
              }
            </>
          }
        </div>
      </div>
    );
  }


}